import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import SymptomContainer from './components/SymptomContainer';
import Checkmark from 'pages/profile/questionnaire/components/Checkmark';

import useSymptomChecker from 'hooks/useSymptomChecker';
import { ENGLISH_OTHER, SPANISH_OTHER } from 'lib/constants';
import { checkSpanishLocale, cn } from 'lib/utils';
import { useSymptomStore } from 'store/symptom';
import { useTranslation } from 'react-i18next';

export default function SymptomOtherSymptom() {
  const { t } = useTranslation();
  const questionData = useSymptomStore((store) => store.questionData)?.data;

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useSymptomChecker();

  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [other, setOther] = useState('');
  const [isNoLoading, setIsNoLoading] = useState(false);

  const englishChoices = JSON.parse(questionData?.eng_choices || '[]');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '[]');

  const options = isSpanish ? spanishChoices : englishChoices;
  const formattedOptions = options.map((c: unknown) =>
    typeof c === 'string' ? c : Object.keys(c as any)[0]
  );

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const selected =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];
      let parsedAnswer = [];
      try {
        parsedAnswer = JSON.parse(selected);
      } catch (error) {
        console.log(error);
      }

      const formattedAnswers: string[] = [];
      parsedAnswer.forEach((option: string) => {
        if (typeof option === 'string') {
          formattedAnswers.push(option);
        } else {
          const entry = Object.entries(option);
          if (entry?.length) {
            formattedAnswers.push(entry[0][0]);
            setOther(entry[0][1] as string);
          }
        }
      });

      setSelectedSymptoms(
        formattedAnswers?.includes('None') ? [] : formattedAnswers
      );
    }
  }, [questionData, isSpanish]);

  const onSelect = (symptom: string) => {
    let selected = [...selectedSymptoms];
    if (selectedSymptoms.includes(symptom)) {
      selected = selected.filter((c) => c !== symptom);
    } else {
      selected.push(symptom);
    }
    setSelectedSymptoms(selected);
    if (!selected.includes(isSpanish ? SPANISH_OTHER : ENGLISH_OTHER)) {
      setOther('');
    }
  };

  const onContinue = () => {
    const formattedSelected = selectedSymptoms.includes(
      isSpanish ? SPANISH_OTHER : ENGLISH_OTHER
    )
      ? selectedSymptoms.map((option) => {
          if ([ENGLISH_OTHER, SPANISH_OTHER].includes(option)) {
            return { [option]: other };
          }

          return option;
        })
      : selectedSymptoms;
    const otherLangSymptoms = formattedSelected.map((option) => {
      if (typeof option !== 'string') {
        return { [isSpanish ? ENGLISH_OTHER : SPANISH_OTHER]: other };
      }

      const index = formattedOptions.indexOf(option);
      return isSpanish ? englishChoices[index] : spanishChoices[index];
    });
    const englishOptions = isSpanish ? otherLangSymptoms : formattedSelected;
    const spanishOptions = isSpanish ? formattedSelected : otherLangSymptoms;

    onSubmit({
      eng_choices: JSON.stringify(englishOptions),
      spanish_choices: JSON.stringify(spanishOptions),
      questionData,
    });
  };

  const onNoSelect = () => {
    setIsNoLoading(true);
    const choice = JSON.stringify(['None']);
    onSubmit({
      eng_choices: choice,
      spanish_choices: choice,
      questionData,
    });
  };

  return (
    <SymptomContainer
      questionData={questionData}
      isLoading={isLoading && !isNoLoading}
      onContinue={onContinue}
      questionNumber={5}
      otherButton={
        <Button
          variant='outline'
          className='px-6 text-gray-500 border-gray-500 h-9'
          onClick={onNoSelect}
          disabled={isNoLoading}
        >
          {isNoLoading && <Loader2 className='mr-2 animate-spin size-4' />}
          {t('none')}
        </Button>
      }
    >
      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6'>
        {formattedOptions?.map((option: string) => {
          const isSelected = selectedSymptoms.includes(option);
          return (
            <div key={option} className='space-y-2'>
              <motion.div
                onClick={() => onSelect(option)}
                className={cn(
                  'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-[#656D76] gap-4 transition-all duration-150',
                  isSelected && 'border-primary text-primary bg-primary/10'
                )}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.15 }}
              >
                <p className='text-sm leading-5'>{option}</p>
                <Checkmark isSelected={isSelected} />
              </motion.div>

              {[SPANISH_OTHER, ENGLISH_OTHER].includes(option) &&
                selectedSymptoms.includes(
                  isSpanish ? SPANISH_OTHER : ENGLISH_OTHER
                ) && (
                  <Input
                    placeholder={t('symptom_placeholder')}
                    value={other}
                    onChange={(e) => setOther(e.target.value)}
                    autoFocus
                    className='text-sm'
                  />
                )}
            </div>
          );
        })}
      </div>
    </SymptomContainer>
  );
}
